import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import phoxyStatic from '../../assets/phoxy-static.png';
import phoxyIdle from '../../assets/phoxy-idle.webp';
import phoxyIdleSmall from '../../assets/phoxy-idle-small.webp';

// Single source of truth for the mascot artwork. Every screen that shows
// Phoxy imports from here so a future art refresh is a one-place swap.
// All three assets share the same 512×640 source framing, so swapping
// animated ↔ static never shifts layout.
export { phoxyStatic, phoxyIdle, phoxyIdleSmall };

/** Display height (px) at or below which the 128×160 encode is still ≥2× crisp. */
const SMALL_ASSET_MAX_SIZE = 80;

export interface PhoxyProps {
  /** Rendered height in px (width follows the 4:5 art ratio). Default 64. */
  size?: number;
  /** Show the 2.5s idle loop (breathing, blink, ear twitch, tail sway). Default true. */
  animated?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Phoxy — the PHOX mascot.
 *
 * Renders the idle-loop animation sized for its slot (a lighter 128×160
 * encode for small inline placements, the 256×320 one above that), and
 * falls back to the static pose when `animated` is false or the user
 * prefers reduced motion. Decorative: always `alt=""`; give the wrapper
 * an aria-label if the surrounding state needs announcing.
 */
export function Phoxy({ size = 64, animated = true, sx }: PhoxyProps) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const showAnimated = animated && !prefersReducedMotion;
  const src = showAnimated
    ? size <= SMALL_ASSET_MAX_SIZE
      ? phoxyIdleSmall
      : phoxyIdle
    : phoxyStatic;

  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{ height: size, width: 'auto', display: 'block', ...sx }}
    />
  );
}

export default Phoxy;
